import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { CashForecastService } from '../../services/cashForecast.service';
import { PERMISSIONS } from '../../constants/permissions';
import {
  FORECAST_CONFIDENCES, ForecastConfidence, ForecastItem, ForecastResponse, ForecastRow,
} from '../../interfaces/cashForecast.interface';
import { formatDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import ForecastSettingsDialog from './ForecastSettingsDialog';
import ForecastAdjustmentsDialog from './ForecastAdjustmentsDialog';

const cell = (n: number): string =>
  n === 0 ? '-' : n.toLocaleString('en-US', { maximumFractionDigits: 0 });

const CONFIDENCE_HINT: Record<ForecastConfidence, string> = {
  FIRM: 'Contractual or on a schedule: cheques, loan instalments, back-to-back maturities',
  EXPECTED: 'Open documents at their due date: customer invoices, supplier bills, deposits',
  ESTIMATE: 'Assumption-driven: export proceeds, payroll, VAT, open purchase orders',
};

interface CellTarget { row: ForecastRow; week: number | null }

const CashForecast: React.FC = () => {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.ACCOUNT_MANAGE);
  const institutionId = AuthService.getSelectedInstitution()!;

  const [from, setFrom] = useState('');
  const [confidence, setConfidence] = useState<ForecastConfidence[]>([...FORECAST_CONFIDENCES]);
  const [grid, setGrid] = useState<ForecastResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [target, setTarget] = useState<CellTarget | null>(null);
  const [items, setItems] = useState<ForecastItem[] | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showAdjustments, setShowAdjustments] = useState(false);

  const params = useMemo(() => ({ from: from || undefined, confidence }), [from, confidence]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setGrid(await CashForecastService.grid(institutionId, params));
    } catch (err) {
      toast.showApiError(err, 'Could not load the cash forecast');
    } finally {
      setLoading(false);
    }
  }, [institutionId, params, toast]);

  useEffect(() => { load(); }, [load]);

  const openCell = async (row: ForecastRow, week: number | null) => {
    setTarget({ row, week });
    setItems(null);
    try {
      setItems(await CashForecastService.items(institutionId, params, row.key, week ?? undefined));
    } catch (err) {
      toast.showApiError(err, 'Could not load the items behind this figure');
      setTarget(null);
    }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      await CashForecastService.exportXlsx(institutionId, params);
    } catch (err) {
      toast.showApiError(err, 'Could not export the forecast');
    } finally {
      setExporting(false);
    }
  };

  const onConfidence = (_: unknown, next: ForecastConfidence[]) => {
    setConfidence(next.length === 0 ? [...FORECAST_CONFIDENCES] : next);
  };

  const sectionRows = (rows: ForecastRow[], weeks: number) => rows.map(r => (
    <TableRow key={r.key} hover>
      <TableCell data-help="banking.cashForecast.row.source" sx={stickyCell}>
        <Tooltip title={r.itemCount === 0 ? 'Nothing expected from this source' : `${r.itemCount} item(s): click the total to list them`}>
          <span>{r.label}</span>
        </Tooltip>
      </TableCell>
      {Array.from({ length: weeks }, (_, i) => (
        <TableCell data-help="banking.cashForecast.action.weekCell" key={i} align="right"
          onClick={r.weekly[i] !== 0 ? () => openCell(r, i + 1) : undefined}
          sx={{ cursor: r.weekly[i] !== 0 ? 'pointer' : 'default', '&:hover': r.weekly[i] !== 0 ? { textDecoration: 'underline' } : undefined }}>
          {cell(r.weekly[i])}
        </TableCell>
      ))}
      <TableCell data-help="banking.cashForecast.action.totalCell" align="right" sx={{ fontWeight: 600, cursor: r.itemCount ? 'pointer' : 'default' }}
        onClick={r.itemCount ? () => openCell(r, null) : undefined}>{cell(r.total)}</TableCell>
    </TableRow>
  ));

  const sumRow = (label: string, values: number[], opts: { bold?: boolean; signColour?: boolean; flags?: boolean[]; sumTotal?: boolean } = {}) => (
    <TableRow sx={{ '& td': { fontWeight: opts.bold ? 700 : 400, borderTop: opts.bold ? 2 : undefined, borderColor: 'divider' } }}>
      <TableCell data-help="banking.cashForecast.row.summary" sx={stickyCell}>{label}</TableCell>
      {values.map((v, i) => (
        <TableCell key={i} align="right"
          sx={theme => ({
            color: opts.signColour && v < 0 ? 'error.main' : undefined,
            bgcolor: opts.flags && opts.flags[i] ? alpha(theme.palette.error.main, 0.14) : undefined,
          })}>
          {cell(v)}
        </TableCell>
      ))}
      {opts.sumTotal
        ? (
          <TableCell align="right" sx={{ color: opts.signColour && values.reduce((a, b) => a + b, 0) < 0 ? 'error.main' : undefined }}>
            {cell(values.reduce((a, b) => a + b, 0))}
          </TableCell>
        )
        : <TableCell />}
    </TableRow>
  );

  return (
    <Box data-help-page="banking.cashForecast.page" sx={{ p: 2 }}>
      <Box data-help-section="banking.cashForecast.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flex: '1 1 260px' }}>13-week cash forecast</Typography>
        <TextField data-help="banking.cashForecast.from" sx={{ flex: '1 1 170px', maxWidth: 210 }} size="small" type="date" label="Forecast from"
          InputLabelProps={{ shrink: true }} value={from} onChange={e => setFrom(e.target.value)}
          helperText={from ? undefined : 'Blank = today'} />
        <ToggleButtonGroup data-help="banking.cashForecast.confidence" size="small" value={confidence} onChange={onConfidence} aria-label="Confidence filter" sx={{ flex: '0 1 auto' }}>
          {FORECAST_CONFIDENCES.map(c => (
            <ToggleButton key={c} value={c} aria-label={c}>
              <Tooltip title={CONFIDENCE_HINT[c]}><span>{humanizeEnum(c)}</span></Tooltip>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Button data-help="banking.cashForecast.export" startIcon={<DownloadIcon />} variant="outlined" onClick={exportXlsx} disabled={exporting || !grid}>Export XLSX</Button>
        <Button data-help="banking.cashForecast.manualLines" variant="outlined" onClick={() => setShowAdjustments(true)}>{canManage ? 'Manual lines' : 'View manual lines'}</Button>
        <Button data-help="banking.cashForecast.assumptions" variant="outlined" onClick={() => setShowSettings(true)}>Assumptions</Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        A read-only projection built from open documents and your assumptions. It posts nothing to the ledger.
        Overdue items are shown in week 1.
      </Alert>

      {loading && !grid && <CircularProgress size={28} />}
      {grid?.warnings.map(w => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}

      {grid && (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'center' }}>
            <Chip label={`Opening cash ${formatMoney(grid.openingBalance, 'BDT')}`} color="primary" variant="outlined" />
            <Chip label={`Minimum cash ${formatMoney(grid.minimumCash, 'BDT')}`} variant="outlined" />
            {grid.odMemo && (
              <Tooltip title="Undrawn overdraft headroom. Shown for information; it is never added to cash.">
                <Chip label={`Overdraft headroom (memo) ${formatMoney(grid.odMemo.headroom, 'BDT')}`} variant="outlined" />
              </Tooltip>
            )}
            <Typography variant="body2" color="text.secondary">
              {formatDate(grid.weeks[0].start)} to {formatDate(grid.horizonEnd)}
            </Typography>
          </Box>

          <TableContainer data-help-section="banking.cashForecast.grid" component={Paper} sx={{ maxHeight: '70vh' }}>
            <Table size="small" stickyHeader aria-label="Cash forecast grid">
              <TableHead>
                <TableRow>
                  <TableCell data-help="banking.cashForecast.col.line" sx={{ ...stickyCell, zIndex: 4 }}>Line</TableCell>
                  {grid.weeks.map(w => (
                    <TableCell data-help="banking.cashForecast.col.week" key={w.index} align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={`${formatDate(w.start)} to ${formatDate(w.end)}`}>
                        <span>W{w.index}<br />{formatDate(w.start).slice(0, 5)}</span>
                      </Tooltip>
                    </TableCell>
                  ))}
                  <TableCell data-help="banking.cashForecast.col.total" align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sumRow('Opening cash', grid.opening)}
                <TableRow><TableCell colSpan={grid.weeks.length + 2} sx={sectionCell}>Inflows</TableCell></TableRow>
                {sectionRows(grid.inflows, grid.weeks.length)}
                {sumRow('Total inflows', grid.totalInflows, { bold: true, sumTotal: true })}
                <TableRow><TableCell colSpan={grid.weeks.length + 2} sx={sectionCell}>Outflows</TableCell></TableRow>
                {sectionRows(grid.outflows, grid.weeks.length)}
                {sumRow('Total outflows', grid.totalOutflows, { bold: true, sumTotal: true })}
                {sumRow('Net cash flow', grid.net, { bold: true, signColour: true, sumTotal: true })}
                {sumRow('Closing cash', grid.closing, { bold: true, signColour: true, flags: grid.belowMinimum })}
              </TableBody>
            </Table>
          </TableContainer>
          {grid.belowMinimum.some(Boolean) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Closing cash falls below the minimum of {formatMoney(grid.minimumCash, 'BDT')} in week{' '}
              {grid.belowMinimum.map((b, i) => (b ? i + 1 : null)).filter(Boolean).join(', ')} (highlighted).
            </Alert>
          )}
        </>
      )}

      <Dialog open={target !== null} onClose={() => setTarget(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {target?.row.label}{target?.week ? `, week ${target.week}` : ', all weeks'}
        </DialogTitle>
        <DialogContent dividers>
          {items === null ? <CircularProgress size={24} /> : items.length === 0 ? (
            <Typography color="text.secondary">No items.</Typography>
          ) : (
            <TableContainer data-help-section="banking.cashForecast.items">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="banking.cashForecast.item.col.date">Date</TableCell><TableCell data-help="banking.cashForecast.item.col.reference">Reference</TableCell><TableCell data-help="banking.cashForecast.item.col.party">Party</TableCell>
                    <TableCell data-help="banking.cashForecast.item.col.amount" align="right">Amount (BDT)</TableCell><TableCell data-help="banking.cashForecast.item.col.confidence">Confidence</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((i, idx) => (
                    <TableRow key={`${i.documentId ?? 'x'}-${idx}`}>
                      <TableCell>{formatDate(i.expectedDate)}</TableCell>
                      <TableCell>
                        {i.deepLink && i.documentRef
                          ? <RouterLink to={i.deepLink}>{i.documentRef}</RouterLink>
                          : (i.documentRef ?? '-')}
                      </TableCell>
                      <TableCell>{i.counterparty ?? '-'}</TableCell>
                      <TableCell align="right">
                        {formatMoney(i.amountBase)}
                        {i.foreignAmount != null && i.currencyCode ? ` (${formatMoney(i.foreignAmount, i.currencyCode)})` : ''}
                      </TableCell>
                      <TableCell><Chip size="small" label={humanizeEnum(i.confidence)} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setTarget(null)}>Close</Button></DialogActions>
      </Dialog>

      {showSettings && (
        <ForecastSettingsDialog institutionId={institutionId} canManage={canManage}
          onClose={() => setShowSettings(false)} onSaved={() => { setShowSettings(false); load(); }} />
      )}
      {showAdjustments && (
        <ForecastAdjustmentsDialog institutionId={institutionId} canManage={canManage}
          onClose={() => setShowAdjustments(false)} onChanged={load} />
      )}
    </Box>
  );
};

const stickyCell = { position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2, minWidth: 220, whiteSpace: 'nowrap' } as const;
const sectionCell = { fontWeight: 700, bgcolor: 'action.hover', textTransform: 'uppercase', fontSize: 12, letterSpacing: 0.5 } as const;

export default CashForecast;
